import React, { useRef, useState, useCallback, useEffect } from 'react'
import classnames from 'classnames/bind'
import { useIntl } from 'umi'
import Style from './index.less'
import { Icon } from '..'
import { rtc, meeting } from '@/models'
import { useIframeBridge } from '@/utils/IframeBridge/useIframeBridge'
import { Tooltip, message } from '@/components'
import { elkLog } from '@/utils/elkReport'
import { handle } from '@/utils/Bridge'

const $c = classnames.bind(Style)

const CodeEditor = React.memo(() => {
  const [lang, setLang] = useState('')
  const [langDropVisible, setLangDropVisible] = useState(false)
  const [hasLangDropBeenShown, setHasLangDrpBeenShown] = useState(false)
  const [filter, setFilter] = useState('')
  const [editorReady, setEditorReady] = useState(false)

  const inputRef = useRef<HTMLInputElement>(null)
  const iframeRef = useRef<HTMLIFrameElement>(null)

  const bridge = useIframeBridge(iframeRef, [])

  const showLangDrop = useCallback(() => {
    setFilter('')
    setLangDropVisible(true)
    setHasLangDrpBeenShown(true)
  }, [])
  const { formatMessage } = useIntl()

  const selectLang = useCallback(
    (lang: string) => {
      setLang(pre => {
        if (pre === lang) {
          bridge?.invoke('setLang', lang)
        } else {
          setLang(lang)
          bridge?.invoke('setLang', lang)
          message.info({
            content: (
              <span className="text">
                <span>{formatMessage({ id: 'SWITCHED_LANG' })}</span>
                <span>{lang}</span>
              </span>
            ),
            noIcon: true,
            duration: 2,
            widthAlign: meeting?.rightPanelShow,
          })
        }
        return lang
      })
    },
    [bridge, formatMessage],
  )

  const onInputBlur = useCallback(() => {
    setLangDropVisible(false)
  }, [])

  useEffect(() => {
    if (bridge && editorReady) {
      bridge.invoke('setTheme', 'material-ocean')
    }
  }, [bridge, editorReady])

  useEffect(() => {
    if (bridge) {
      bridge.handle('ready', () => {
        console.log('code editor ready')
        elkLog('代码编辑器页面ready')
        setEditorReady(true)
        meeting.setCodeEditorLoading(false)
      })
      bridge.handle('onLangChange', (lang: string) => {
        setLang(lang)
        message.info({
          content: (
            <span className="text">
              <span>{formatMessage({ id: 'SWITCHED_LANG' })}</span>
              <span>{lang}</span>
            </span>
          ),
          noIcon: true,
          duration: 2,
        })
      })
    }
  }, [bridge, formatMessage])

  useEffect(() => {
    if (langDropVisible) {
      inputRef.current?.focus()
    }
  }, [langDropVisible])

  return (
    <div className={$c('code-editor')}>
      {!window.isMobile && (
        <div className={$c('operation-header')}>
          <Tooltip title={formatMessage({ id: 'CLOSE' })} className="R48Gnh0rxF" placement="bottom">
            <div className={$c('icon-wrapper')}>
              <Icon className={$c('icon')} name="icon-close_nor" onClick={meeting.closeEditor} />
            </div>
          </Tooltip>
          <span className={$c('split-line')} />
          <div className={$c('lang-select-container', { active: langDropVisible })}>
            {langDropVisible ? (
              <div className={$c('lang-input-container')}>
                <input
                  ref={inputRef}
                  type="text"
                  className={$c('lang-input')}
                  placeholder={`${lang}`}
                  onBlur={onInputBlur}
                  onInput={e => {
                    setFilter((e.target as HTMLInputElement).value.trim())
                  }}
                />
                <Icon className={$c('icon-search')} name="icon-search" />
              </div>
            ) : (
              <div className={$c('lang')} onClick={showLangDrop}>
                <span className={$c('text', { selected: !!lang })}>{lang || '请选择语言环境'}</span>
                <Icon className={$c('icon-arrowdown')} name="icon-arrow_line_down" />
              </div>
            )}
            <div
              className={$c(
                'lang-item-container',
                hasLangDropBeenShown ? (langDropVisible ? 'visible' : 'invisible') : '',
              )}
            >
              {[
                'Plain Text',
                'APL',
                'ASN.1',
                'ASP.NET',
                'Brainfuck',
                'C',
                'C#',
                'C++',
                'Clojure',
                'ClojureScript',
                'Closure Stylesheets (GSS)',
                'CMake',
                'Cobol',
                'Common Lisp',
                'CQL',
                'Crystal',
                'CSS',
                'Cypher',
                'Cython',
                'D',
                'diff',
                'DTD',
                'Dylan',
                'ECL',
                'edn',
                'Eiffel',
                'Elm',
                'Embedded JavaScript',
                'Embedded Ruby',
                'Erlang',
                'F#',
                'Factor',
                'Forth',
                'Fortran',
                'Gas',
                'Gherkin',
                'Go',
                'Groovy',
                'HAML',
                'Haskell',
                'Haskell (Literate)',
                'Haxe',
                'HTML',
                'HXML',
                'IDL',
                'Java',
                'Java Server Pages',
                'JavaScript',
                'Jinja2',
                'JSON-LD',
                'Julia',
                'Kotlin',
                'LaTeX',
                'LESS',
                'LiveScript',
                'Lua',
                'Markdown',
                'Mathematica',
                'mbox',
                'Modelica',
                'mscgen',
                'msgenny',
                'MUMPS',
                'NSIS',
                'Objective-C',
                'Objective-C++',
                'OCaml',
                'Octave',
                'Oz',
                'Pascal',
                'PEG.js',
                'Perl',
                'Pig',
                'PLSQL',
                'PowerShell',
                'Properties files',
                'ProtoBuf',
                'Pug',
                'Puppet',
                'Python',
                'Q',
                'R',
                'reStructuredText',
                'RPM Spec',
                'Ruby',
                'Rust',
                'SAS',
                'Sass',
                'Scala',
                'Scheme',
                'SCSS',
                'Sieve',
                'Smalltalk',
                'Smarty',
                'SML',
                'Soy',
                'SPARQL',
                'SQL',
                'Squirrel',
                'Stylus',
                'Swift',
                'SystemVerilog',
                'Tcl',
                'Textile',
                'TOML',
                'troff',
                'TTCN',
                'TTCN_CFG',
                'Turtle',
                'TypeScript',
                'TypeScript-JSX',
                'VB.NET',
                'VBScript',
                'Velocity',
                'Verilog',
                'VHDL',
                'Web IDL',
                'WebAssembly',
                'XQuery',
                'xu',
                'Yacas',
                'Z80',
              ]
                .filter(item => {
                  if (filter) {
                    return item.toLowerCase().includes(filter.toLowerCase())
                  }
                  return true
                })
                .map(item => (
                  <div
                    key={item}
                    className={$c('lang-item', { selected: lang === item })}
                    onMouseDown={() => {
                      selectLang(item)
                    }}
                  >
                    <span className={$c('item-text')}>{item}</span>
                    {lang === item && <Icon className={$c('icon-check')} name="icon-check" />}
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}
      <div className={$c('iframe')}>
        <iframe
          id="codeEditorIframe"
          ref={iframeRef}
          className={$c('editor-frame')}
          title="code-editor"
          src={`https://coledit.popo.netease.com/is/${rtc.cid}?default_mode=${encodeURIComponent('JavaScript')}`}
        />
      </div>
    </div>
  )
})

export default CodeEditor
